import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Checkbox from "@material-ui/core/Checkbox";
import ListItemText from "@material-ui/core/ListItemText";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";

import toastError from "../../errors/toastError";
import {
  getUserGroup,
  createUserGroup,
  updateUserGroup,
  listAssignableUsers
} from "../../services/access.api";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  row: { display: "flex", gap: 12, marginBottom: 12 },
  col: { flex: 1, minWidth: 0 },
  section: { marginTop: 4 },
  campoTitulo: { fontWeight: 600, fontSize: 12, marginBottom: 4 },
  nota: {
    color: theme.palette.text.secondary,
    fontSize: 11,
    display: "block",
    marginBottom: 12
  },
  vacio: { opacity: 0.6 },
  itemNombre: { fontWeight: 600, fontSize: 13 },
  itemSecundario: { fontSize: 11, color: theme.palette.text.secondary },
  estado: {
    fontSize: 9,
    fontWeight: 700,
    color: "#fff",
    padding: "1px 6px",
    borderRadius: 10,
    marginLeft: 8
  },
  conectado: { backgroundColor: "#25d366" },
  desconectado: { backgroundColor: "#e1306c" },
  grupoCabecera: {
    fontWeight: 700,
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
    paddingTop: 4,
    paddingBottom: 4
  },
  membersBox: {
    maxHeight: 168,
    overflowY: "auto",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4
  }
}));

/**
 * Alta y edición de una subagrupación.
 *
 * El rol por miembro es la pieza que pidió el cliente: la misma persona puede
 * ser USER en un equipo y SUPERVISOR en otro (doc 0380 D11). Dejarlo vacío
 * significa "hereda su rol base", que es el caso normal.
 */
/**
 * Menú anclado al campo y con tope de altura.
 *
 * Por defecto MUI centra el desplegable sobre el `Select` y lo deja crecer hasta
 * el alto de la ventana: con 20 departamentos tapaba la pantalla entera. Es el
 * mismo ajuste que usa el selector de conexión de la agenda.
 */
const menuAnclado = {
  getContentAnchorEl: null,
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
  PaperProps: { style: { maxHeight: 260 } }
};

const UserGroupModal = ({ open, onClose, groupId, onSaved }) => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [queues, setQueues] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);

  const [form, setForm] = useState({
    name: "",
    description: "",
    supervisorName: "",
    queueIds: [],
    whatsappIds: [],
    connections: [],
    members: {} // { userId: roleId | "" }
  });

  useEffect(() => {
    if (!open) return;

    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [usersRes, queuesRes, whatsRes, groupRes] = await Promise.all([
          listAssignableUsers(),
          api.get("/queue"),
          api.get("/whatsapp"),
          groupId ? getUserGroup(groupId) : Promise.resolve({ data: null })
        ]);

        if (!active) return;

        setUsers(usersRes?.data ?? []);
        setQueues(queuesRes?.data ?? []);
        setWhatsapps(whatsRes?.data ?? []);

        const group = groupRes?.data;
        if (group) {
          setForm({
            name: group.name ?? "",
            description: group.description ?? "",
            supervisorName: group.supervisorName ?? "",
            connections: group.connections ?? [],
            queueIds: (group.queues ?? []).map((q) => q.queueId),
            whatsappIds: (group.declaredConnections ?? []).map((w) => w.id),
            members: Object.fromEntries(
              (group.members ?? []).map((m) => [m.userId, m.roleId ?? ""])
            )
          });
        } else {
          setForm({
            name: "",
            description: "",
            supervisorName: "",
            queueIds: [],
            whatsappIds: [],
            connections: [],
            members: {}
          });
        }
      } catch (err) {
        if (active) toastError(err);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [open, groupId]);

  const toggleMember = useCallback((userId) => {
    setForm((prev) => {
      const members = { ...prev.members };
      if (Object.prototype.hasOwnProperty.call(members, userId)) {
        // Ya no hay supervisor que proteger aqui: se deduce del rol del miembro
        // y se asigna desde su ficha (decision del cliente, 2026-08-19).
        delete members[userId];
      } else {
        members[userId] = "";
      }
      return { ...prev, members };
    });
  }, []);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || null,
        queueIds: form.queueIds,
        whatsappIds: form.whatsappIds,
        members: Object.entries(form.members).map(([userId, roleId]) => ({
          userId: Number(userId),
          roleId: roleId ? Number(roleId) : null
        }))
      };

      if (groupId) await updateUserGroup(groupId, payload);
      else await createUserGroup(payload);

      toast.success(groupId ? "Subagrupación actualizada" : "Subagrupación creada");
      onSaved?.();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const memberIds = Object.keys(form.members).map(Number);

  // Los que YA son del equipo arriba; el resto debajo. Dentro de cada bloque se
  // conserva el orden alfabético que trae la vista.
  const enElEquipo = users.filter((u) => memberIds.includes(u.userId));
  const fueraDelEquipo = users.filter((u) => !memberIds.includes(u.userId));

  /**
   * La conexión con la misma cara que en la agenda: 📱, nombre y estado.
   * `detalle` añade la segunda línea (nombre de WhatsApp o número), que solo
   * cabe dentro del desplegable.
   */
  const filaConexion = (w, detalle = false) => (
    <div style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
      <span style={{ marginRight: 6 }} role="img" aria-label="WhatsApp">
        📱
      </span>
      <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span className={classes.itemNombre}>{w.name}</span>
          <span
            className={`${classes.estado} ${
              w.status === "CONNECTED" ? classes.conectado : classes.desconectado
            }`}
          >
            {w.status || "DISCONNECTED"}
          </span>
        </div>
        {detalle && (w.waName || w.number) && (
          <span className={classes.itemSecundario}>{w.waName || w.number}</span>
        )}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        {groupId ? "Editar subagrupación" : "Nueva subagrupación"}
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <div style={{ textAlign: "center", padding: 32 }}>
            <CircularProgress />
          </div>
        ) : (
          <>
            <div className={classes.row}>
              <TextField
                className={classes.col}
                label="Nombre"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                variant="outlined"
                size="small"
                autoFocus
              />
              {/*
                El supervisor no aparece aquí: se deduce del ámbito del rol de
                cada miembro y se asigna desde su ficha (cliente, 2026-08-19).
                Abajo, en la lista, se ve quién es.
              */}
              {/* Mismo reparto que la fila de abajo, para que Nombre y
                  Departamento queden a la misma medida. */}
              <TextField
                className={classes.col}
                label="Descripción"
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
                variant="outlined"
                size="small"
              />
            </div>

            {/*
              Departamento y conexión, una fila y dos columnas: son los dos ejes
              del mismo recorte —qué trabajo ve el equipo— y leerlos juntos dice
              más que uno debajo del otro.
            */}
            <div className={classes.row}>
              <div className={classes.col}>
                <div className={classes.campoTitulo}>Departamento</div>
                <FormControl variant="outlined" size="small" fullWidth>
                  <Select
                    multiple
                    displayEmpty
                    value={form.queueIds}
                    MenuProps={menuAnclado}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, queueIds: e.target.value }))
                    }
                    renderValue={(selected) =>
                      selected.length === 0 ? (
                        <span className={classes.vacio}>Ninguno</span>
                      ) : (
                        queues
                          .filter((q) => selected.includes(q.id))
                          .map((q) => q.name)
                          .join(", ")
                      )
                    }
                  >
                    {queues.map((q) => (
                      <MenuItem dense key={q.id} value={q.id}>
                        <Checkbox
                          color="primary"
                          size="small"
                          checked={form.queueIds.includes(q.id)}
                        />
                        <ListItemText
                          primary={q.name}
                          primaryTypographyProps={{ className: classes.itemNombre }}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              {/*
                CONEXIÓN — el eje que faltaba (migración 344). Con solo
                departamentos, dos equipos que atienden el mismo —«Ventas Perú»
                y «Ventas México», cada uno con su número— veían los mismos
                tickets. Vacío = todas.
              */}
              <div className={classes.col}>
                <div className={classes.campoTitulo}>Conexión</div>
                <FormControl variant="outlined" size="small" fullWidth>
                  <Select
                    multiple
                    displayEmpty
                    value={form.whatsappIds}
                    MenuProps={menuAnclado}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, whatsappIds: e.target.value }))
                    }
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return <span className={classes.vacio}>Todas</span>;
                      }
                      if (selected.length === 1) {
                        const w = whatsapps.find((x) => x.id === selected[0]);
                        return w ? filaConexion(w) : null;
                      }
                      return (
                        <span
                          style={{
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {whatsapps
                            .filter((w) => selected.includes(w.id))
                            .map((w) => w.name)
                            .join(", ")}
                        </span>
                      );
                    }}
                  >
                    {whatsapps.map((w) => (
                      <MenuItem dense key={w.id} value={w.id}>
                        <Checkbox
                          color="primary"
                          size="small"
                          checked={form.whatsappIds.includes(w.id)}
                        />
                        {filaConexion(w, true)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>

            {/*
              El aviso solo aparece cuando hay algo que avisar, y en una línea:
              ninguna conexión declara esos departamentos, así que sus tickets
              entran sin departamento y el equipo no los ve.
            */}
            {form.queueIds.length > 0 && form.connections.length === 0 && (
              <span className={classes.nota}>
                Ninguna conexión declara estos departamentos.
              </span>
            )}

            <div className={classes.section}>
              <div className={classes.campoTitulo}>Miembros</div>
              <span className={classes.nota}>
                Quien ya trabaja en otro equipo aparece bloqueado. El rol se
                define en la ficha de cada persona.
              </span>
              <div className={classes.membersBox}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>Usuario</TableCell>
                      <TableCell style={{ width: 150 }}>Situación</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/*
                      Dos bloques, no una lista plana: primero quién ES del
                      equipo y luego el resto. Con 45 personas, saber quiénes
                      son los cuatro miembros obligaba a recorrer toda la lista
                      buscando casillas marcadas.
                    */}
                    {[
                      { clave: "miembros", filas: enElEquipo },
                      { clave: "resto", filas: fueraDelEquipo }
                    ].map(({ clave, filas }) =>
                      filas.length === 0 ? null : (
                        <React.Fragment key={clave}>
                          <TableRow>
                            <TableCell colSpan={3} className={classes.grupoCabecera}>
                              {clave === "miembros"
                                ? `Miembros del equipo (${filas.length})`
                                : `Resto de usuarios (${filas.length})`}
                            </TableCell>
                          </TableRow>
                          {filas.map((u) => {
                      const included = memberIds.includes(u.userId);
                      // Cardinalidad 1:1 (cliente 2026-08-19): quien ya trabaja
                      // en OTRO equipo no se recluta desde aquí. Hay que sacarlo
                      // del suyo primero — moverlo en silencio le cambiaría los
                      // departamentos que atiende sin que nadie lo pidiera.
                      const enOtro =
                        u.userGroupId && Number(u.userGroupId) !== Number(groupId);

                      return (
                        <TableRow key={u.userId} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              color="primary"
                              size="small"
                              checked={included}
                              disabled={enOtro}
                              onChange={() => toggleMember(u.userId)}
                            />
                          </TableCell>
                          <TableCell>
                            {u.userName}
                            {u.isSupervisor && included && (
                              <Typography
                                variant="caption"
                                color="primary"
                                style={{ marginLeft: 8, fontWeight: 700 }}
                              >
                                supervisor
                              </Typography>
                            )}
                          </TableCell>
                          {/*
                            La columna dejó de decidir y pasó a informar: el rol
                            se elige en la ficha de la persona, y el equipo aquí
                            solo dice qué está ocurriendo.
                          */}
                          <TableCell>
                            {enOtro ? (
                              <Typography variant="caption" color="error">
                                ya está en {u.userGroupName}
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="textSecondary">
                                {u.roleName ?? "sin rol"}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                          })}
                        </React.Fragment>
                      )
                    )}
                  </TableBody>
                </Table>
              </div>
              <Typography variant="caption" color="textSecondary">
                {memberIds.length} miembro(s) · {form.queueIds.length} departamento(s)
              </Typography>
            </div>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={saving || loading}
        >
          {saving ? <CircularProgress size={20} /> : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserGroupModal;
